// hooks/useDelayedVisibility.ts
import { useEffect, useRef, useState } from "react";

/**
 * Visar `true` först efter showDelayMs, och håller sig kvar i minst minVisibleMs
 * när den väl blivit synlig – även om `busy` blir false under tiden.
 */
export function useDelayedVisibility(busy: boolean, showDelayMs = 200, minVisibleMs = 300) {
  const [visible, setVisible] = useState(false);
  const firstShownAtRef = useRef<number | null>(null);
  const showTimerRef = useRef<number | null>(null);
  const hideTimerRef = useRef<number | null>(null);

  useEffect(() => {
    // rensa timers på varje förändring
    if (showTimerRef.current) window.clearTimeout(showTimerRef.current);
    if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);

    if (busy) {
      // vänta innan vi visar
      showTimerRef.current = window.setTimeout(() => {
        setVisible((prev) => {
          if (!prev) firstShownAtRef.current = Date.now();
          return true;
        });
      }, showDelayMs);
    } else {
      if (!visible) {
        // var ändå inte synlig – inget att göra
        return;
      }
      // räkna ut hur länge den varit synlig
      const shownAt = firstShownAtRef.current ?? Date.now();
      const elapsed = Date.now() - shownAt;
      const remaining = Math.max(minVisibleMs - elapsed, 0);

      hideTimerRef.current = window.setTimeout(() => {
        firstShownAtRef.current = null;
        setVisible(false);
      }, remaining);
    }

    return () => {
      if (showTimerRef.current) window.clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    };
  }, [busy, showDelayMs, minVisibleMs, visible]);

  return visible;
}
